/**
 * Builds a Google Maps URL that drops a pin on the given query.
 *
 * The query can be a street address or a "lat,lng" pair - Google resolves both,
 * so coordinate-mode projects need no separate code path.
 *
 * @param {string} query - address or "lat,lng" string
 * @returns {string|null} the URL, or null when there is nothing to search for
 */
export const getGoogleMapsSearchUrl = (query) => {
  if (typeof query !== 'string') return null;
  const trimmed = query.trim();
  if (!trimmed) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(trimmed)}`;
};

/**
 * Builds a Google Maps URL for a coordinate pair.
 *
 * (0, 0) is rejected: the codebase already treats it as "no location captured"
 * rather than a point in the Atlantic - see calcProjectCoords in scheduleHelpers.
 *
 * @param {number|string} latitude
 * @param {number|string} longitude
 * @returns {string|null} the URL, or null when the coordinates are missing or invalid
 */
export const getGoogleMapsCoordsUrl = (latitude, longitude) => {
  if (latitude === null || latitude === undefined || latitude === '') return null;
  if (longitude === null || longitude === undefined || longitude === '') return null;

  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat === 0 && lng === 0) return null;

  return getGoogleMapsSearchUrl(`${lat},${lng}`);
};

/**
 * Opens a Google Maps search for the given query in a new tab.
 *
 * Needed where a plain anchor cannot do the job: markerwithlabel intercepts the
 * click on a map pin's label and calls preventDefault/stopPropagation on it
 * (see its cAbortEvent), which kills both the anchor's navigation and React's
 * document-level event delegation. Callers there hook the marker's own click
 * event and open the tab themselves.
 *
 * @param {string} query - address or "lat,lng" string
 * @returns {boolean} whether a tab was opened
 */
export const openGoogleMaps = (query) => {
  const url = getGoogleMapsSearchUrl(query);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};
